import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { Input, Switch, Table, Button, Tooltip, notification, Avatar, Modal, Popconfirm } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { Comment } from '@ant-design/compatible';
import { useTranslation } from 'react-i18next';
import api from '../../../api/api';
import Grocefy from '../../../assets/images/grocefyLogoAlone.png';

function TimeSheetTypes() {
  const { t, i18n } = useTranslation();
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [updatingType, setUpdatingType] = useState(false);
  const [showModal, setShowModal] = useState(false);

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function getTypes() {
    setLoadingTypes(true);
    api
      .get('timesheet/types')
      .then((response) => {
        setTypes(response.data.data.results);
        setLoadingTypes(false);
      })
      .catch((error) => {
        console.error(error);
        setLoadingTypes(false);
      });
  }

  function createType() {
    setUpdatingType(true);
    api
      .post('timesheet/types', selectedType)
      .then((response) => {
        setUpdatingType(false);
        if (response.data.success) {
          showMessage(response.data.message);
          setTypes(response.data.data.results);
          setShowModal(false);
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        console.error(error);
        showMessage(error.message);
        setUpdatingType(false);
      });
  }

  function updateType() {
    setUpdatingType(true);
    api
      .put('timesheet/types', selectedType)
      .then((response) => {
        setUpdatingType(false);
        if (response.data.success) {
          showMessage(response.data.message);
          setTypes(response.data.data.results);
          setShowModal(false);
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        console.error(error);
        showMessage(error.message);
        setUpdatingType(false);
      });
  }

  function deleteType() {
    setUpdatingType(true);
    api
      .delete(`timesheet/types/${selectedType.id}`)
      .then((response) => {
        setUpdatingType(false);
        if (response.data.success) {
          showMessage(response.data.message);
          setTypes(response.data.data.results);
          setShowModal(false);
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        console.error(error);
        showMessage(error.message);
        setUpdatingType(false);
      });
  }

  useEffect(() => {
    getTypes();
  }, []);

  useEffect(() => {
    if (!showModal) {
      setSelectedType(null);
    }
  }, [showModal]);

  return (
    <div>
      <PageHeader
        className="mb-4 px-0"
        title={t('timesheet_types')}
      />
      <div>
        <div className="flex space-x-2">
          <Button
            type="primary"
            size="small"
            className="ml-auto mt-2"
            onClick={() => {
              setSelectedType({
                isDefault: false,
                nameEn: '',
                nameEs: ''
              });
              setShowModal(true);
            }}
          >
            {t('create_new')}
          </Button>
          <Button
            type="primary"
            size="small"
            loading={loadingTypes}
            className="ml-auto mt-2"
            onClick={() => getTypes}
          >
            {t('refresh')}
          </Button>
        </div>
        <Table
          pagination={false}
          size="small"
          className="mt-2"
          bordered
          rowKey="id"
          loading={loadingTypes}
          dataSource={types}
          columns={[
            {
              title: `${t('name')} (EN)`,
              key: 'nameen',
              align: 'center',
              className: 'text-xs',
              render: (text) => <p>{text?.nameEn}</p>,
            },
            {
              title: `${t('name')} (ES)`,
              key: 'namees',
              align: 'center',
              className: 'text-xs',
              render: (text) => <p>{text?.nameEs}</p>,
            },
            {
              title: t('default'),
              key: 'default',
              align: 'center',
              className: 'text-xs',
              render: (text) => <FontAwesomeIcon className={`${text.isDefault ? 'text-green-400' : 'text-red-400'} text-2xl`} icon={text.isDefault ? faCheckCircle : faTimesCircle} />,
            },
            {
              title: t('actions'),
              key: 'actions',
              align: 'center',
              className: 'text-xs',
              render: (text) => (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    setSelectedType(text);
                    setShowModal(true);
                  }}
                >
                  {t('edit')}
                </Button>
              ),
            },
          ]}
        />
      </div>
      <Modal
        title={!selectedType?.id ? t('create_new_type') : t('update_type')}
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={[
          <Popconfirm
            title={t('delete_type_prompt')}
            okText={t('yes')}
            cancelText={t('no')}
            onConfirm={() => deleteType()}
            onCancel={() => { }}
          >
            <Button
              key="delete"
              type="primary"
              danger
              className={`${selectedType?.id ? '' : 'hidden'} mr-1`}
              disabled={updatingType}
            >
              {t('delete')}
            </Button>,
          </Popconfirm>,
          <Button
            key="close"
            type="primary"
            danger
            disabled={updatingType}
            onClick={() => setShowModal(false)}
          >
            {t('close')}
          </Button>,
          <Button
            key="ok"
            type="primary"
            disabled={updatingType || selectedType?.nameEn.length === 0 || selectedType?.nameEs.length === 0}
            loading={updatingType}
            onClick={() => (!selectedType?.id ? createType() : updateType())}
          >
            {!selectedType?.id ? t('create') : t('update')}
          </Button>
        ]}
      >
        <div className="space-y-4">
          <div>
            <p>{t('name')} (EN)</p>
            <Input
              disabled={updatingType}
              value={selectedType?.nameEn}
              onChange={(obj) => setSelectedType({
                ...selectedType,
                nameEn: obj.target.value
              })}
            />
          </div>
          <div>
            <p>{t('name')} (ES)</p>
            <Input
              disabled={updatingType}
              value={selectedType?.nameEs}
              onChange={(obj) => setSelectedType({
                ...selectedType,
                nameEs: obj.target.value
              })}
            />
          </div>
          <div>
            <p>{t('default')}</p>
            <Switch
              disabled={updatingType}
              value={selectedType?.isDefault}
              onChange={(obj) => setSelectedType({
                ...selectedType,
                isDefault: obj
              })}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default TimeSheetTypes;
